import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Check, X, Crown, Zap, Eye, Vote, MessageSquare, Heart, Trophy, ArrowLeft, Loader2, Users, Smile, Bookmark, Ban } from "lucide-react";
import Navbar from "@/components/Navbar";

// Feature matrix matching exact tier rules
const FEATURE_MATRIX = [
  { 
    name: "Watch Debates", 
    icon: Eye, 
    free: true, access: true, pro: true 
  },
  { 
    name: "Vote on Debates", 
    icon: Vote, 
    free: true, access: true, pro: true 
  },
  { 
    name: "Like Debates", 
    icon: Heart, 
    free: true, access: true, pro: true 
  },
  { 
    name: "Support Debaters", 
    icon: Heart, 
    free: true, access: true, pro: true,
    note: "Follow alternative"
  },
  { 
    name: "Comment on Debates", 
    icon: MessageSquare, 
    free: true, access: true, pro: true 
  },
  { 
    name: "Ad-Free Experience", 
    icon: Ban, 
    free: false, access: true, pro: true 
  },
  { 
    name: "Debate Groups Access", 
    icon: Users, 
    free: false, access: true, pro: true 
  },
  { 
    name: "Create Debates", 
    icon: Zap, 
    free: false, access: false, pro: true 
  },
  { 
    name: "Create Groups", 
    icon: Users, 
    free: false, access: false, pro: true 
  },
  { 
    name: "Pro-Only Emotes", 
    icon: Smile, 
    free: false, access: false, pro: true,
    note: "👍👎😂😡👏😴❤️💪"
  },
  { 
    name: "Create Highlights", 
    icon: Bookmark, 
    free: false, access: false, pro: true 
  },
  { 
    name: "Leaderboard Eligible", 
    icon: Trophy, 
    free: false, access: false, pro: true 
  },
  { 
    name: "Subscription Badge", 
    icon: Crown, 
    free: false, access: "A", pro: "P",
    isText: true
  },
];

const SubscriptionPage = () => {
  const { user, token, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [processingTier, setProcessingTier] = useState(null);

  useEffect(() => {
    fetchData();
    
    // Check if returning from Stripe checkout
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [tiersRes, subRes] = await Promise.all([
        axios.get(`${API}/subscriptions/tiers`),
        user ? axios.get(`${API}/subscriptions/my-subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        }) : Promise.resolve({ data: null })
      ]);
      setTiers(tiersRes.data.tiers);
      setCurrentSubscription(subRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 10;
    
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.data.payment_status === "paid") {
          toast.success("Subscription activated! Welcome to " + res.data.tier + "!");
          const userRes = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(userRes.data);
          setCheckingPayment(false);
          navigate("/subscription", { replace: true });
          fetchData();
          return;
        }
        
        if (res.data.status === "expired") {
          toast.error("Payment session expired. Please try again.");
          setCheckingPayment(false);
          navigate("/subscription", { replace: true });
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.error("Payment verification timed out. Please check your email.");
          setCheckingPayment(false);
          navigate("/subscription", { replace: true });
        }
      } catch (e) {
        console.error(e);
        setCheckingPayment(false);
      }
    };
    
    poll();
  };

  const handleSubscribe = async (tierId) => {
    if (!user) {
      toast.error("Please log in to subscribe");
      navigate("/login");
      return;
    }
    
    if (tierId === "free") return;
    
    setProcessingTier(tierId);
    try {
      const res = await axios.post(`${API}/subscriptions/checkout`, {
        tier: tierId,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start checkout");
      setProcessingTier(null);
    }
  };

  const getTierIcon = (tierId) => {
    switch (tierId) {
      case "free": return <Eye className="w-8 h-8" />;
      case "access": return <Users className="w-8 h-8" />;
      case "pro": return <Crown className="w-8 h-8" />;
      default: return <Zap className="w-8 h-8" />;
    }
  };

  const getTierColor = (tierId) => {
    switch (tierId) {
      case "free": return "text-muted-foreground";
      case "access": return "text-blue-400";
      case "pro": return "text-amber-500";
      default: return "text-primary";
    }
  };

  // Corrected feature lists for each tier
  const getTierFeatures = (tierId) => {
    switch (tierId) {
      case "free":
        return [
          "Watch all debates",
          "Vote on debates",
          "Like debates",
          "Support debaters",
          "Comment on debates"
        ];
      case "access":
        return [
          "All Free features",
          "Ad-free experience",
          "Debate group access",
          "Badge: A"
        ];
      case "pro":
        return [
          "All Access features",
          "Create debates",
          "Create groups",
          "Pro emotes 👍👎😂😡👏😴❤️💪",
          "Create highlights",
          "Leaderboard eligible",
          "Badge: P"
        ];
      default:
        return [];
    }
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-24">
          <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
          <p className="text-muted-foreground">
            {checkingPayment ? "Verifying your payment..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <Link 
          to="/browse" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 group text-sm"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Debates
        </Link>

        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            Choose Your <span className="text-primary">Plan</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            Unlock the full Spardom experience. From spectating to competing on the leaderboard.
          </p>
          
          {currentSubscription && (
            <div className="mt-4 inline-flex items-center gap-2 bg-card/50 border border-white/10 rounded-full px-4 py-2">
              <span className="text-sm text-muted-foreground">Current plan:</span>
              <Badge variant={currentSubscription.tier === "pro" ? "default" : currentSubscription.tier === "access" ? "secondary" : "outline"}>
                {currentSubscription.tier_name}
              </Badge>
              {currentSubscription.is_admin && (
                <Badge variant="destructive">Admin</Badge>
              )}
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {tiers.map((tier) => {
            const isCurrentTier = currentSubscription?.tier === tier.id;
            const isAdmin = currentSubscription?.is_admin;
            const features = getTierFeatures(tier.id);
            
            return (
              <div 
                key={tier.id}
                data-testid={`tier-card-${tier.id}`}
                className={`relative bg-card/30 rounded-xl border p-6 transition-all ${
                  tier.id === "pro" 
                    ? "border-amber-500/50 scale-[1.02] shadow-lg shadow-amber-500/10" 
                    : tier.id === "access"
                    ? "border-blue-500/30"
                    : "border-white/10"
                } ${isCurrentTier ? "ring-2 ring-primary" : ""}`}
              >
                {tier.id === "pro" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500 text-black">Most Popular</Badge>
                  </div>
                )}
                
                {isCurrentTier && (
                  <div className="absolute -top-3 right-4">
                    <Badge variant="outline" className="border-primary text-primary">Current</Badge>
                  </div>
                )}

                <div className={`mb-4 ${getTierColor(tier.id)}`}>
                  {getTierIcon(tier.id)}
                </div>

                <h3 className="text-xl font-bold mb-1">{tier.name}</h3>
                
                {/* Badge indicator */}
                {tier.id === "free" ? (
                  <p className="text-xs text-muted-foreground mb-3">No badge</p>
                ) : tier.id === "access" ? (
                  <p className="text-xs text-blue-400 mb-3">Badge: <span className="font-bold bg-blue-500/20 px-1.5 py-0.5 rounded">A</span></p>
                ) : (
                  <p className="text-xs text-amber-500 mb-3">Badge: <span className="font-bold bg-amber-500/20 px-1.5 py-0.5 rounded">P</span></p>
                )}
                
                <div className="mb-1">
                  <span className="text-3xl font-bold">${tier.price.toFixed(2)}</span>
                  {tier.price > 0 && <span className="text-muted-foreground text-sm">/{tier.period}</span>}
                </div>
                
                {/* Intro pricing note for Pro */}
                {tier.intro_pricing && (
                  <div className="mb-3">
                    <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-500">
                      🎉 Founding Pro Price
                    </Badge>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Standard: ${tier.standard_price?.toFixed(2)}/mo
                    </p>
                  </div>
                )}
                
                {/* Ad-supported note for Free tier */}
                {tier.id === "free" && (
                  <p className="text-xs text-yellow-500/80 mb-3">⚠️ Ad-supported</p>
                )}
                
                {tier.id !== "free" && (
                  <p className="text-xs text-green-500 mb-3">✓ Ad-free</p>
                )}

                <ul className="space-y-2 mb-6 min-h-[140px]">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${getTierColor(tier.id)}`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  data-testid={`subscribe-${tier.id}`}
                  onClick={() => handleSubscribe(tier.id)}
                  disabled={isCurrentTier || isAdmin || tier.id === "free" || processingTier === tier.id}
                  className={`w-full h-11 font-bold uppercase tracking-wider text-sm ${
                    tier.id === "pro" 
                      ? "bg-amber-500 text-black hover:bg-amber-400" 
                      : tier.id === "access"
                      ? "bg-blue-500 text-white hover:bg-blue-400"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {processingTier === tier.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isAdmin ? (
                    "Admin Access"
                  ) : isCurrentTier ? (
                    "Current Plan"
                  ) : tier.id === "free" ? (
                    "Free Forever"
                  ) : (
                    `Get ${tier.name}`
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Features Comparison Table */}
        <div>
          <h2 className="text-xl font-bold text-center mb-6">Compare Features</h2>
          <div className="bg-card/30 rounded-xl border border-white/10 overflow-x-auto">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-3 text-sm font-medium">Feature</th>
                  <th className="text-center p-3 text-sm font-medium">Free</th>
                  <th className="text-center p-3 text-sm font-medium text-blue-400">Access</th>
                  <th className="text-center p-3 text-sm font-medium text-amber-500">Pro</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_MATRIX.map((feature, idx) => (
                  <tr key={idx} className="border-b border-white/5 last:border-0">
                    <td className="p-3 text-sm">
                      <div className="flex items-center gap-2">
                        <feature.icon className="w-4 h-4 text-muted-foreground" />
                        <span>{feature.name}</span>
                      </div>
                      {feature.note && (
                        <span className="text-[10px] text-muted-foreground ml-6">{feature.note}</span>
                      )}
                    </td>
                    <td className="text-center p-3">
                      {feature.isText ? (
                        feature.free ? <span className="text-xs font-bold">{feature.free}</span> : <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      ) : feature.free ? (
                        <Check className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      )}
                    </td>
                    <td className="text-center p-3">
                      {feature.isText ? (
                        feature.access ? <span className="text-xs font-bold text-blue-400 bg-blue-500/20 px-1.5 py-0.5 rounded">{feature.access}</span> : <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      ) : feature.access ? (
                        <Check className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      )}
                    </td>
                    <td className="text-center p-3">
                      {feature.isText ? (
                        feature.pro ? <span className="text-xs font-bold text-amber-500 bg-amber-500/20 px-1.5 py-0.5 rounded">{feature.pro}</span> : <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      ) : feature.pro ? (
                        <Check className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <X className="w-4 h-4 text-muted-foreground/50 mx-auto" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            Questions? Contact us at <a href="mailto:support@spardom.com" className="text-primary hover:underline">support@spardom.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
